import React, { useState, useEffect } from 'react';
import {
    Row,
    Col,
    Card,
    Button,
    Form,
    Spinner,
    InputGroup
} from 'react-bootstrap';
import { FaEdit, FaSave, FaTimes } from 'react-icons/fa';
import { MdDoubleArrow } from 'react-icons/md';
import { toast } from 'react-hot-toast';
import { extractErrorMessage } from '../../constants/common';
import instance from "../../services/baseService";
import { IoIosEye, IoIosEyeOff } from "react-icons/io";
const ClientCredentials = ({ clientId }) => {
    const [loading, setLoading] = useState(true);
    const [accounts, setAccounts] = useState([]);
    const [credentialsData, setCredentialsData] = useState({});
    const [originalCredentialsData, setOriginalCredentialsData] = useState({});
    const [editModes, setEditModes] = useState({});
    const [savingStates, setSavingStates] = useState({});

    const [showPasswordMap, setShowPasswordMap] = useState({});

    const togglePassword = (id) => {
        setShowPasswordMap(prev => ({ ...prev, [id]: !prev[id] }));
    };
    // Fetch accounts list from backend
    const fetchAccounts = async () => {
        setLoading(true);
        try {

            const response = await instance.get(`/api/v1/client/accounts/list/${clientId}`, {
                page: 1,
                perpage: 25
            });
            if (response.data?.type === 'success') {
                setAccounts(response?.data?.data?.list || []);
            }

        } catch (error) {
            toast.error("Faild to fetch credentials");
            setLoading(false);
        } finally {
            setLoading(false);
        }
    };

    // Fetch client credentials for each account
    const fetchClientCredentials = async () => {
        if (!clientId || accounts.length === 0) return;

        try {
            const response = await instance.post(`/api/v1/client/credentials/${clientId}`);
            if (response.data?.type === 'success') {
                const responseAccounts = response.data.data.document_types || [];

                // Transform the response to match component's expected structure
                const transformedData = {};
                responseAccounts.forEach(account => {
                    transformedData[account.document_type_id] = {
                        name: account.credentials?.username || '',
                        password: account.credentials?.password || '',
                    };
                });

                setCredentialsData(transformedData);
                setOriginalCredentialsData(JSON.parse(JSON.stringify(transformedData)));
            }

        } catch (error) {
            toast.error("Faild to fetch credentials");
        }
    };

    // Initialize component
    useEffect(() => {
        fetchAccounts();
    }, []);

    useEffect(() => {
        if (accounts.length > 0) {
            fetchClientCredentials();
        }
    }, [accounts, clientId]);

    // Handle input changes for specific account
    const handleInputChange = (accountId, field, value) => {
        setCredentialsData(prev => ({
            ...prev,
            [accountId]: {
                ...prev[accountId],
                [field]: value
            }
        }));
    };

    // Handle edit mode toggle for specific account
    const handleEditToggle = (accountId) => {
        setShowPasswordMap(prev => ({ ...prev, [accountId]: true }));
        setEditModes(prev => {
            const newEditModes = { ...prev };

            if (!newEditModes[accountId]) {
                const item = credentialsData[accountId];
                if (item !== undefined) {
                    setOriginalCredentialsData(prevOriginal => ({
                        ...prevOriginal,
                        [accountId]: JSON.parse(JSON.stringify(item))
                    }));
                }
            }

            newEditModes[accountId] = !newEditModes[accountId];
            return newEditModes;
        });
    };

    // Handle save for specific account
    const handleSave = async (accountId) => {
        setSavingStates(prev => ({ ...prev, [accountId]: true }));

        try {
            const accountCredentials = credentialsData[accountId];

            const response = await instance.post('/api/v1/client/credentials/create-update', {
                document_type_id: accountId,
                client_id: clientId,
                username: accountCredentials.name,
                password: accountCredentials.password,
            });

            if (response.data?.type === 'success') {
                toast.success('Credentials updated successfully');
                setEditModes(prev => ({ ...prev, [accountId]: false }));
                // Update the original data
                setOriginalCredentialsData(prev => ({
                    ...prev,
                    [accountId]: JSON.parse(JSON.stringify(accountCredentials))
                }));
            }

        } catch (error) {
            const errorMessage = extractErrorMessage(error);
            toast.error(errorMessage);
        } finally {
            setShowPasswordMap(prev => ({ ...prev, [accountId]: false }));
            setSavingStates(prev => ({ ...prev, [accountId]: false }));
        }
    };

    // Handle cancel for specific account
    const handleCancel = (accountId) => {
        setShowPasswordMap(prev => ({ ...prev, [accountId]: false }));
        setCredentialsData(prev => ({
            ...prev,
            [accountId]: JSON.parse(JSON.stringify(originalCredentialsData[accountId] || {
                id: null,
                name: '',
                password: '',
            }))
        }));

        setEditModes(prev => ({ ...prev, [accountId]: false }));
    };

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center py-5">
                <div className="text-center">
                    <Spinner animation="border" role="status" className="mb-3">
                        <span className="visually-hidden">Loading...</span>
                    </Spinner>
                    <p className="text-muted">Loading client credentials...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="p-3">
            {accounts.length === 0 ? (
                <div className="text-center py-5">
                    <p className="text-muted">No Documents added.</p>
                </div>
            ) : (
                <Row className="gy-4">
                    {accounts.map((account, index) => {
                        const credentials = credentialsData[account.id] || {
                            name: '',
                            password: '',
                        };

                        const isEditing = editModes[account.id] || false;
                        const isSaving = savingStates[account.id] || false;

                        return (
                            <Col xs={12} key={account.id}>
                                <Card className="shadow-sm border">
                                    <Card.Header className="bg-light d-flex justify-content-between align-items-center">
                                        <h6 className="mb-0 text-primary fw-semibold">
                                            {index + 1}. {account?.name || "No Title"}
                                        </h6>

                                        {/* Action buttons for each account */}
                                        <div className="d-flex align-items-center gap-2">
                                            {isEditing ? (
                                                <>
                                                    <Button
                                                        variant="primary"
                                                        size="sm"
                                                        className="fw-bold d-flex align-items-center justify-content-center"
                                                        onClick={() => handleSave(account.id)}
                                                        disabled={isSaving || !credentials.name?.trim() || !credentials.password?.trim()}
                                                        style={{ width: '80px' }}
                                                    >
                                                        {isSaving ? 'Saving...' : 'Save'}
                                                    </Button>
                                                    <Button
                                                        variant="secondary"
                                                        size="sm"
                                                        className="fw-bold d-flex align-items-center justify-content-center"
                                                        onClick={() => handleCancel(account.id)}
                                                        disabled={isSaving}
                                                        style={{ width: '80px' }}
                                                    >
                                                        Cancel
                                                    </Button>
                                                </>
                                            ) : (
                                                <Button
                                                    variant="link"
                                                    size="sm"
                                                    className="d-flex align-items-center text-decoration-none"
                                                    onClick={() => handleEditToggle(account.id)}
                                                >
                                                    <FaEdit className="me-1" size={12} />
                                                    Edit
                                                </Button>
                                            )}
                                        </div>
                                    </Card.Header>
                                    <Card.Body>
                                        <Row className="g-3">
                                            <Col md={4}>
                                                <Form.Group>
                                                    <Form.Label className="fw-bold">User name</Form.Label>
                                                    <Form.Control
                                                        type="text"
                                                        placeholder="Enter name"
                                                        value={credentials.name}
                                                        disabled={!isEditing}
                                                        onChange={(e) => handleInputChange(account.id, 'name', e.target.value)}
                                                    />
                                                </Form.Group>
                                            </Col>
                                            <Col md={4}>
                                                <Form.Group>
                                                    <Form.Label className="fw-bold">Password</Form.Label>
                                                    <InputGroup>
                                                        <Form.Control
                                                            type={showPasswordMap[account.id] ? "text" : "password"}
                                                            placeholder="Enter password"
                                                            value={credentials.password}
                                                            disabled={!isEditing}
                                                            onChange={(e) =>
                                                                handleInputChange(account.id, "password", e.target.value)
                                                            }
                                                        />
                                                        {/* Show the eye icon only when editing is disabled */}
                                                        <InputGroup.Text
                                                            onClick={() => togglePassword(account.id)}
                                                            style={{ cursor: "pointer", background: "transparent", borderLeft: "none" }}
                                                        >
                                                            {showPasswordMap[account.id] ? <IoIosEyeOff /> : <IoIosEye />}
                                                        </InputGroup.Text>
                                                    </InputGroup>
                                                </Form.Group>
                                            </Col>
                                        </Row>
                                    </Card.Body>
                                </Card>
                            </Col>
                        );
                    })}
                </Row>
            )}
        </div>
    );
};

export default ClientCredentials;